import React from 'react'
import { UseFormReturn } from 'react-hook-form'
import { H2, Line, Button, Flex } from '../../lib/components'
import { CAMPAIGN_RULE_OPTIONS, CampaignFormValues } from './campaign-form-types'
import './campaign-form.scss'

type CampaignFormPresenterProps = {
  form: UseFormReturn<CampaignFormValues>
  isSubmitting: boolean
  onSubmit: (data: CampaignFormValues) => Promise<void>
  mode?: 'create' | 'edit'
}

function CampaignFormPresenter({
  form,
  isSubmitting,
  onSubmit,
  mode = 'create',
}: CampaignFormPresenterProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form

  const heading = mode === 'edit' ? 'Edit campaign' : 'Create Campaign'
  const submitText =
    mode === 'edit'
      ? isSubmitting
        ? 'Saving...'
        : 'Save changes'
      : isSubmitting
      ? 'Creating...'
      : 'Create campaign'

  return (
    <Flex variant='column-center'>
      <H2 classes={['mb-24']} text={heading} />

      <form className='campaign-form' onSubmit={handleSubmit(onSubmit)} noValidate>
        <Line />

        <div className='campaign-form__field'>
          <label className='campaign-form__label' htmlFor='campaign-code'>
            Code <span className='campaign-form__required'>*</span>
          </label>
          <input
            id='campaign-code'
            className={`campaign-form__input ${errors.code ? 'campaign-form__input--error' : ''}`}
            placeholder='Promotional code (3–128 characters)'
            maxLength={128}
            autoComplete='off'
            {...register('code', {
              required: 'Code is required',
              minLength: { value: 3, message: 'Code must be at least 3 characters' },
              maxLength: { value: 128, message: 'Code must be at most 128 characters' },
            })}
          />
          {errors.code && <span className='campaign-form__error'>{errors.code.message}</span>}
        </div>

        <div className='campaign-form__field'>
          <label className='campaign-form__label' htmlFor='campaign-description'>
            Description
          </label>
          <textarea
            id='campaign-description'
            className={`campaign-form__input campaign-form__textarea ${
              errors.description ? 'campaign-form__input--error' : ''
            }`}
            placeholder='Optional description for internal use'
            maxLength={2000}
            rows={4}
            {...register('description', {
              maxLength: { value: 2000, message: 'Description must be at most 2000 characters' },
            })}
          />
          {errors.description && (
            <span className='campaign-form__error'>{errors.description.message}</span>
          )}
        </div>

        <div className='campaign-form__field'>
          <label className='campaign-form__label' htmlFor='campaign-rule'>
            Rule <span className='campaign-form__required'>*</span>
          </label>
          <select
            id='campaign-rule'
            className={`campaign-form__input ${errors.ruleId ? 'campaign-form__input--error' : ''}`}
            {...register('ruleId', { required: 'Please select a rule' })}
          >
            {CAMPAIGN_RULE_OPTIONS.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
          <span className='campaign-form__hint'>
            Sent as <code>rules</code> on the create payload (currently{' '}
            <code>{'{"waive_dealing_charges":true}'}</code> for this option).
          </span>
          {errors.ruleId && <span className='campaign-form__error'>{errors.ruleId.message}</span>}
        </div>

        <div className='campaign-form__field'>
          <div className='campaign-form__checkbox-row'>
            <input
              id='campaign-active'
              type='checkbox'
              className='campaign-form__checkbox'
              {...register('is_active')}
            />
            <label className='campaign-form__label' htmlFor='campaign-active' style={{ margin: 0 }}>
              Active
            </label>
          </div>
          <span className='campaign-form__hint'>
            When unchecked, the code is created as inactive.
          </span>
        </div>

        <div className='campaign-form__field'>
          <label className='campaign-form__label' htmlFor='campaign-valid-from'>
            Valid from
          </label>
          <input
            id='campaign-valid-from'
            type='datetime-local'
            className={`campaign-form__input ${
              errors.valid_from ? 'campaign-form__input--error' : ''
            }`}
            {...register('valid_from')}
          />
          {errors.valid_from && (
            <span className='campaign-form__error'>{errors.valid_from.message}</span>
          )}
          <span className='campaign-form__hint'>Leave blank for no start bound.</span>
        </div>

        <div className='campaign-form__field'>
          <label className='campaign-form__label' htmlFor='campaign-valid-until'>
            Valid until
          </label>
          <input
            id='campaign-valid-until'
            type='datetime-local'
            className={`campaign-form__input ${
              errors.valid_until ? 'campaign-form__input--error' : ''
            }`}
            {...register('valid_until')}
          />
          {errors.valid_until && (
            <span className='campaign-form__error'>{errors.valid_until.message}</span>
          )}
          <span className='campaign-form__hint'>Leave blank for no end bound.</span>
        </div>

        <div className='campaign-form__field'>
          <label className='campaign-form__label' htmlFor='campaign-max-redemptions'>
            Max redemptions
          </label>
          <input
            id='campaign-max-redemptions'
            type='number'
            min={1}
            step={1}
            className={`campaign-form__input ${
              errors.max_redemptions ? 'campaign-form__input--error' : ''
            }`}
            placeholder='Unlimited if left blank'
            {...register('max_redemptions', {
              validate: (v) => {
                if (v === '' || v === undefined || v === null) return true
                const n = Number(v)
                if (!Number.isInteger(n) || n < 1) {
                  return 'Must be a whole number of at least 1'
                }
                return true
              },
            })}
          />
          {errors.max_redemptions && (
            <span className='campaign-form__error'>{errors.max_redemptions.message}</span>
          )}
        </div>

        <div className='campaign-form__actions'>
          <Button
            buttonType='submit'
            handleClick={() => {}}
            text={submitText}
            variant='primary-btn'
            isDisabled={isSubmitting}
          />
        </div>
      </form>
    </Flex>
  )
}

export { CampaignFormPresenter }
